import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { ThemeId } from '../lib/types.ts';

export type ThemePreference = ThemeId | 'system' | 'high-contrast';
export type MotionPreference = 'system' | 'reduce' | 'full';

export interface ThemeState {
  preference: ThemePreference;
  /** the theme applied to <html data-theme> */
  resolved: ThemeId | 'high-contrast';
  reducedMotion: boolean;
  setPreference: (p: ThemePreference) => void;
  setMotion: (m: MotionPreference) => void;
  motion: MotionPreference;
}

const ThemeContext = createContext<ThemeState | null>(null);

function media(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(query).matches;
}

function useMedia(query: string): boolean {
  const [match, setMatch] = useState(() => media(query));
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const on = () => setMatch(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return match;
}

export interface ThemeProviderProps {
  children: ReactNode;
  preference?: ThemePreference;
  motion?: MotionPreference;
  onPreferenceChange?: (p: ThemePreference) => void;
  onMotionChange?: (m: MotionPreference) => void;
  /** element receiving data-theme / data-motion (default <html>) */
  target?: HTMLElement | null;
}

/**
 * Applies `data-theme` (midnight | dusk | light | high-contrast) and `data-motion` to <html>.
 * "system" follows prefers-color-scheme (dark → midnight, light → light).
 */
export function ThemeProvider({
  children,
  preference: controlled,
  motion: controlledMotion,
  onPreferenceChange,
  onMotionChange,
  target,
}: ThemeProviderProps) {
  const [local, setLocal] = useState<ThemePreference>(controlled ?? 'midnight');
  const [localMotion, setLocalMotion] = useState<MotionPreference>(controlledMotion ?? 'system');
  const preference = controlled ?? local;
  const motion = controlledMotion ?? localMotion;
  const prefersLight = useMedia('(prefers-color-scheme: light)');
  const prefersReduced = useMedia('(prefers-reduced-motion: reduce)');
  const resolved: ThemeId | 'high-contrast' = preference === 'system' ? (prefersLight ? 'light' : 'midnight') : preference;
  const reducedMotion = motion === 'reduce' || (motion === 'system' && prefersReduced);

  useEffect(() => {
    const el = target ?? (typeof document !== 'undefined' ? document.documentElement : null);
    if (!el) return;
    el.dataset.theme = resolved;
    el.dataset.motion = reducedMotion ? 'reduce' : 'full';
    el.style.colorScheme = resolved === 'light' ? 'light' : 'dark';
  }, [resolved, reducedMotion, target]);

  const value = useMemo<ThemeState>(
    () => ({
      preference,
      resolved,
      reducedMotion,
      motion,
      setPreference: p => {
        setLocal(p);
        onPreferenceChange?.(p);
      },
      setMotion: m => {
        setLocalMotion(m);
        onMotionChange?.(m);
      },
    }),
    [preference, resolved, reducedMotion, motion, onPreferenceChange, onMotionChange],
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used inside <ThemeProvider>');
  return ctx;
}

/** True when animations should collapse to instant state changes. */
export function useReducedMotion(): boolean {
  const ctx = useContext(ThemeContext);
  const prefersReduced = useMedia('(prefers-reduced-motion: reduce)');
  return ctx ? ctx.reducedMotion : prefersReduced;
}
